<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.campaign_create') }}
      <template v-slot:right>
        <router-link :to="{ name: 'user-campaign-list'}"><b-button size="sm" variant="outline-primary">{{ $t('btn.campaign.back_to_campaign_list') }}</b-button></router-link>
      </template>
    </page-header>

    <page-card>
      <template v-slot:header>
        {{ $t('page.campaign.campaign_parameters') }}
      </template>
      <template v-slot:content>

        <b-form-group id="name" :label="$t('form.campaign.label.name')" label-for="input-1">
          <b-form-input
              id="input-2"
              v-model="form.name"
              required
          ></b-form-input>
          <p v-if="$v.form.name.$dirty && $v.form.name.$invalid" class="text-danger">{{ $t('error.required') }}</p>
        </b-form-group>

        <b-form-group id="title" :label="$t('form.campaign.label.title')" label-for="input-1">
          <b-form-input
              id="input-2"
              v-model="form.title"
              required
          ></b-form-input>
          <p v-if="$v.form.title.$dirty && $v.form.title.$invalid" class="text-danger">{{ $t('error.required') }}</p>
        </b-form-group>

        <b-form-group id="sendersName" :label="$t('form.campaign.label.senders_name')" label-for="input-1">
          <b-form-input
              id="input-2"
              v-model="form.sendersName"
              required
          ></b-form-input>
          <p v-if="$v.form.sendersName.$dirty && $v.form.sendersName.$invalid" class="text-danger">{{ $t('error.required') }}</p>
        </b-form-group>

        <b-form-group id="sendersEmail" :label="$t('form.campaign.label.senders_email')" label-for="input-1">
          <b-form-input
              id="input-2"
              v-model="form.sendersEmail"
              required
          ></b-form-input>
          <p v-if="$v.form.sendersEmail.$dirty && $v.form.sendersEmail.$invalid && !$v.form.sendersEmail.required" class="text-danger">{{ $t('error.required') }}</p>
          <p v-if="$v.form.sendersEmail.$dirty && $v.form.sendersEmail.$invalid && !$v.form.sendersEmail.email" class="text-danger">{{ $t('error.email') }}</p>
        </b-form-group>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.campaign.target') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="name"
                  :label="$t('filters.label.campaign.segment.name')"
                  :placeholder="$t('filters.placeholder.campaign.segment.name')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="createdAt"
                  open-direction="left"
                  :label="$t('filters.campaign.createdAt')">
              </filter-date-range-picker>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-12 d-flex justify-content-end">
              <b-button class="mr-2" @click="resetFilters()" variant="secondary">{{ $t('filters.btn.clear') }}</b-button>
              <b-button @click="applyFilters()" variant="primary">{{ $t('filters.btn.apply') }}</b-button>
            </div>
          </div>

          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
          <div class="table-responsive">
            <b-table striped
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-desc.sync="orderDirection"
                     :empty-text="$t('table.no_results')"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template #cell(id)="data">
                <div class="d-flex align-items-center">
                  <b-checkbox v-model="form.segmentIds"
                              :key="data.value"
                              :value="data.value">
                  </b-checkbox>
                  {{ data.item.id | shortId }}
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
        </div>
        <p v-if="$v.form.segmentIds.$dirty && $v.form.segmentIds.$invalid" class="text-danger mt-3">{{ $t('error.required') }}</p>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.campaign.content') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-12">
              <editor
                  :apiKey="editorApiKey"
                  :init="{
                    height: 500,
                    plugins: editorPlugins,
                    toolbar: editorToolbar
                  }"
                  v-model="form.content"
              />
            </div>
          </div>
          <p v-if="$v.form.content.$dirty && $v.form.content.$invalid" class="text-danger mt-3">{{ $t('error.required') }}</p>
        </div>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.campaign.display_options') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <b-form-group
                  :label="$t('campaign.form.status')"
                  label-for="displayDate"
              >
                <b-form-select
                    style="height: 43px;"
                    v-model="form.status"
                    :options="statusOptions">
                </b-form-select>
              </b-form-group>
              <p v-if="$v.form.status.$dirty && $v.form.status.$invalid" class="text-danger">{{ $t('error.required') }}</p>
            </div>
            <div class="col-4">
              <b-form-group
                  :label="$t('campaign.form.campaign_time')"
                  label-for="displayDate"
              >
                <b-form-select
                    style="height: 43px;"
                    v-model="instantCampaign"
                    @input="resetSendingTime"
                    :options="timeOptions">
                </b-form-select>
              </b-form-group>
            </div>
            <div v-if="!instantCampaign" class="col-4">
              <b-form-group
                  :label="$t('campaign.form.sending_time')"
                  label-for="displayDate"
              >
                <datetime
                    v-model="form.sendingTime"
                    type="datetime"
                    input-class="form-control"
                    value-zone="UTC"
                    zone="UTC"
                    format="yyyy-MM-dd HH:mm:ss"
                    :phrases="{ok: $t('campaign.btn.date_time.continue'), cancel: $t('campaign.btn.date_time.exit')}"
                    :hour-step="1"
                    :minute-step="1"
                    :week-start="7"
                    auto
                ></datetime>
                <p v-if="incorrectDate" class="text-danger">{{ $t('campaign.error.incorrect_date') }}</p>
              </b-form-group>
            </div>
          </div>

          <div class="row mt-5">
            <div class="col-12">
              <div class="d-flex justify-content-center w-100">
                <b-button @click="goToCampaignList" type="button" variant="secondary" class="w-25 mr-2">{{$t('btn.campaign.cancel')}}</b-button>
                <b-button @click="onSubmit" type="button" variant="primary" class="w-25">{{$t('btn.campaign.save')}}</b-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </page-card>
  </div>
</template>

<script>
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";
import {required, email} from "vuelidate/lib/validators";
import Editor from "@tinymce/tinymce-vue";
import moment from "moment";
import { Datetime } from 'vue-datetime';

//TODO: import / load / get locale the right way
let locale = locale ? locale : "en";

import {Settings} from 'luxon'
Settings.defaultLocale = locale ? locale : "en";

export default {
  props: ['apiKey'],
  components: {
    PageCard,
    PageHeader,
    GridPagination,
    FilterInput,
    FilterDateRangePicker,
    datetime: Datetime,
    'editor': Editor
  },
  data() {
    return {
      perPage: 10,
      currentPage: 1,
      selectedRows: [],
      orderDirection: '',
      instantCampaign: true,
      actionType: '',
      statusOptions: [
        {
          text: this.$t('campaign.option.draft'),
          value: 'draft'
        },
        {
          text: this.$t('campaign.option.active'),
          value: 'active'
        }
      ],
      timeOptions: [
        {
          text: this.$t('campaign.option.today_time'),
          value: true
        },
        {
          text: this.$t('campaign.option.certain_time'),
          value: false
        }
      ],
      form: {
        name: '',
        title: '',
        sendersName: '',
        sendersEmail: '',
        content: '',
        segmentIds: [],
        sendingTime: '',
        status: ''
      },
      buttonStatus: null,
      filterQuery: [],
      fields: [
        {key: 'id', label: this.$t('table.header.campaign.segment_id')},
        {key: 'name', label: this.$t('table.header.campaign.segment_name'), sortable: false},
        {key: 'ruleTypes', label: this.$t('table.header.campaign.segment_ruleTypes'), sortable: false},
        {key: 'isStatic', label: this.$t('table.header.campaign.segment_isStatic'), sortable: false},
        {key: 'lastTriggered', label: this.$t('table.header.campaign.segment_lastTriggered'), sortable: false},
      ],
      editorApiKey: '2a3y3y6ckc55ceyj8yvqoicrwnruvsf32dtctkkgop19qea3',
      editorPlugins: [
        'advlist autolink lists link image charmap',
        'searchreplace visualblocks code fullscreen',
        'print preview anchor insertdatetime media',
        'paste code help wordcount table', 'code'
      ],
      editorToolbar: 'undo redo | formatselect | bold italic | \
        alignleft aligncenter alignright | \
        code bullist numlist outdent indent | help'
    };
  },
  validations: {
    form: {
      name: {
        required
      },
      title: {
        required
      },
      sendersName: {
        required
      },
      sendersEmail: {
        required,
        email
      },
      content: {
        required
      },
      segmentIds: {
        required
      },
      status: {
        required
      }
    }
  },
  computed: {
    isBusy: function () {
      return this.$store.getters["userCampaign/isBusy"];
    },
    items: function () {
      return this.$store.getters["userCampaign/items"];
    },
    totalCount: function () {
      return this.$store.getters["userCampaign/totalCount"];
    },
    incorrectDate: function() {
      return moment(this.form.sendingTime).utc().isBefore(moment().utc());
    }
  },
  mounted() {
    this.loadItems();

    if (this.$route.params && this.$route.params.id) {
      this.actionType = this.$route.params.actionType;
      this.form.name = this.$route.params.name;
      this.form.title = this.$route.params.title;
      this.form.content = this.$route.params.content;
      this.form.sendersName = this.$route.params.sendersName;
      this.form.sendersEmail = this.$route.params.sendersEmail;
      this.form.status = this.$route.params.status;
      this.form.sendingTime = this.$route.params.sendingTime;
      this.form.sentDate = this.$route.params.sentDate;
      this.form.segmentIds = this.$route.params.segments.map(segment => {
        return segment.id;
      });

      this.instantCampaign = false;
    }
  },
  methods: {
    resetSendingTime() {
      if(this.instantCampaign) {
        this.form.sendingTime = null;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.loadItems();
    },
    resetFilters() {
      this.$root.$emit('resetFilters');
      this.filterQuery = [];
      this.applyFilters();
    },
    onSubmit() {
      if(!this.$v.$invalid && !this.incorrectDate) {
        const payload = {
          ...this.form,
          id: this.$route.params.id ? this.$route.params.id : null,
          actionType: this.actionType,
          sendingTime: this.form.sendingTime ? moment(this.form.sendingTime).utc().format('YYYY-MM-DD HH:mm:ss') : moment().utc().add(5, 'minutes').format('YYYY-MM-DD HH:mm:ss')
        }

        this.$store.dispatch('userCampaign/saveCampaign', payload).then(() => {
          this.goToCampaignList();
        });
      } else {
        this.$v.$touch();
      }
    },
    goToCampaignList() {
      this.$router.push({ path: `user-campaign-list` });
    },
    setIsApprovalRequired(value) {
      this.form.isApprovalRequired = value;
    },
    loadItems() {
      this.$store.dispatch('userCampaign/loadSegments', {
        params: {
          offset: this.perPage * (this.currentPage - 1),
          limit: this.perPage
        },
        filters: this.filterQuery,
        sort: this.createSortQuery(),
        id: this.$route.params.id
      });
    },
    paginationReload(data) {
      this.currentPage = data.currentPage;
      this.perPage = data.perPage;
      this.loadItems();
    },
    // backToSegmentList() {
    //   this.$router.push({ name: 'user-segment-list' });
    // },
    createSortQuery() {
      // let sorting;
      // if (this.orderColumn) {
      //   if (this.orderDirection === true) {
      //     sorting = encodeURIComponent('+' + this.orderColumn);
      //   } else {
      //     sorting = encodeURIComponent('-' + this.orderColumn);
      //   }
      //   return '&sort[]=' + sorting;
      // }
      return '';
    }
  }
}
</script>
